import React, { useContext, useEffect } from 'react';
import { useParams, useLocation, useHistory } from 'react-router-dom';
import { Text } from '@bighealth/react-limbix-ui';
import { generate } from 'short-uuid';
import { ThemeProvider } from '@material-ui/core';

import { useQuery } from '@apollo/client';

import { Footer } from './components/Footer/Footer';
import { Header } from './components/Header/Header';
import { OnboardingFlowContext, OnboardingFlowProvider } from './OnboardingFlowContext';
import Styled from './OnboardingPage.styles';
import { OnboardingParams } from './types';
import { defaultCaregiverFlow, defaultSleepioFlow } from './userFlows';

import { SPARK_PRODUCT } from './utils/constants';

import { ConsentWidget } from './components/ConsentWidget/ConsentWidget';

import {
  getPdtUid, getTheme, isDaylight, setSiteTitleAndIcon,
} from '@/utils/productHelpers';
import { ELIGIBILITY_CONFIGURATION_QUERY } from '@/apollo/queries';
import { useConsent } from '@/hooks/useConsent';
import { logError } from '@/utils/logError';
import SpringHealthHeader from '@/components/SpringHealthHeader/SpringHealthHeader';

const OnboardingPage: React.FC = () => {
  useConsent();
  const { slug, product } = useParams<OnboardingParams>();
  const location = useLocation();
  const history = useHistory();
  const {
    currentStep,
    steps,
    setUid,
    setSteps,
    eligibilityFormCustomization,
    setEligibilityFormCustomization,
    updateFormData,
    resetFormData,
  } = useContext(OnboardingFlowContext);

  const {
    loading,
    data,
    error,
  } = useQuery(ELIGIBILITY_CONFIGURATION_QUERY, {
    variables: {
      organizationSlug: slug,
      pdtUid: getPdtUid(product),
    },
  });

  // The optional chaining above stops the render crashing on a null config, which is the fix.
  // This is the other half: without it a failed query is indistinguishable from an org we
  // genuinely do not recognise, and the member sees the same screen either way with nothing
  // recorded. The slug is the org, not the member — safe to attach.
  useEffect(() => {
    if (error) {
      logError('[OnboardingPage]', 'eligibility configuration query failed', error, {
        level: 'warning',
        extra: { slug, product },
      });
    }
  }, [error, slug, product]);

  useEffect(() => {
    if (data?.eligibilityConfiguration?.isValid) {
      setUid(generate());
      const params = new URLSearchParams(location.search);
      const auto = params.get('autoSelect');
      const isSpark = product === SPARK_PRODUCT;

      if (isSpark && auto === 'dependent') {
        resetFormData();
        updateFormData({ personEnrolling: 'caregiver' as const });
        setSteps(defaultCaregiverFlow.slice(1));
        params.delete('autoSelect');
        const clean = params.toString();
        history.replace({
          pathname: location.pathname,
          search: clean ? `?${clean}` : '',
          hash: location.hash,
        });
      } else {
        setSteps(product === SPARK_PRODUCT ? defaultCaregiverFlow : defaultSleepioFlow);
      }
      setEligibilityFormCustomization({
        ...eligibilityFormCustomization,
        showZipCode: data?.eligibilityConfiguration.collectZipcodeForEligibility,
        showEmployeeId: data?.eligibilityConfiguration.hasEmployeeId,
        showAddressField: data?.eligibilityConfiguration.hasAddressField,
        employeeIdFieldRename: data?.eligibilityConfiguration.employeeIdFieldRename,
        employeeIdValidationEnabled: data?.eligibilityConfiguration.employeeIdValidationEnabled,
        employeeIdAllowedChars: data?.eligibilityConfiguration.employeeIdAllowedChars,
        employeeIdMinLength: data?.eligibilityConfiguration.employeeIdMinLength,
        employeeIdMaxLength: data?.eligibilityConfiguration.employeeIdMaxLength,
        employeeIdValidationErrorMessage: data?.eligibilityConfiguration.employeeIdValidationErrorMessage,
        employeeIdExample: data?.eligibilityConfiguration.employeeIdExample,
      });
      setSiteTitleAndIcon(product);
    } else {
      setSteps([]);
    }
  }, [data?.eligibilityConfiguration]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    return false;
  };

  if (loading) {
    return null;
  }

  return (
    <Styled.PageContainer>
      {steps.length === 0 ? (
        <Text
          marginTop="100px"
          fontSize="48px"
        >
          {/* TODO: This isn't the best error message.
          Talk to product and design about this
          error message when the slug isn't found */}
          {'It looks like we don\'t have an eligibility flow for this org yet... check your url'}
        </Text>
      ) : (
        <>
          {/* Reuse the existing Daylight header; every other product keeps the shared Header */}
          {isDaylight(product) ? <SpringHealthHeader product="daylight" /> : <Header />}
          <Styled.PageContentContainer>
            <Styled.StepContainer>
              <form onSubmit={(e) => handleSubmit(e)}>
                {currentStep.element}
              </form>
            </Styled.StepContainer>
          </Styled.PageContentContainer>
        </>
      )}
      <ConsentWidget />
      <Footer />
    </Styled.PageContainer>
  );
};

// There wasn't a good place to add this context so it's add here, as a wrapper around the OnboardingPage
export const OnboardingPageWithProvider: React.FC = () => {
  const { product } = useParams<OnboardingParams>();
  return (
    <ThemeProvider theme={getTheme(product)}>
      <OnboardingFlowProvider>
        <OnboardingPage />
      </OnboardingFlowProvider>
    </ThemeProvider>
  );
};
